import Text from '../../componentsNew/atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../componentsNew/enums';
import ButtonNew from '../../componentsNew/molecules/button/Button';
import { useTranslation } from 'react-i18next';
import styles from './SettingsPage.module.css';
import { SidePanel } from '../../componentsNew/organisms/side-panel';
import { InputField } from '../../componentsNew/organisms/input-field';
import SkeletonComponent from '../../components/SkeletonComponent';
import Label from '../../componentsNew/atoms/label/Label';
import { Data, FormErrors } from '../../componentsNew/molecules/form/Form';
import { MailingSettingsFormData } from '../../api/agency/settings/fetchMailingSettings';
import { useRef, useState } from 'react';
import FormError from '../../componentsNew/molecules/form/FormError';
import { HorizontalTabs } from '../../componentsNew/molecules/horizontal-tabs';
import { TabData } from '../../componentsNew/atoms/tab';
import { TextArea } from '../../componentsNew/atoms/text-area';
import useAuth from '../../hooks/useAuth';
import MailSendLineIcon from '../../componentsNew/atoms/icons/MailSendLineIcon';
import sendTestMail from '../../api/agency/settings/sendTestMail/sendTestMail';
import DraftIcon from '../../componentsNew/atoms/icons/DraftIcon';
import { FlagIcon } from '../../componentsNew/atoms/icons/flags';

interface EmailTemplatesContentProps {
    storedData: MailingSettingsFormData | null;
    formData: Data;
    formErrors: FormErrors[];
    isLoading?: boolean;
}

const EmailTemplatesContent = ({
    storedData,
    formData,
    formErrors,
    isLoading = false
}: EmailTemplatesContentProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const settingsSectionRef = useRef<HTMLDivElement>(null);

    const [ rejectionTemplateLangActiveTab, setRejectionTemplateLangActiveTab ] = useState<string>('nl');
    const [ enrichmentTemplateLangActiveTab, setEnrichmentTemplateLangActiveTab ] = useState<string>('nl');

    const languageTabs: TabData[] = [
        {
            icon: <FlagIcon.Dutch/>,
            label: 'Nederlands',
            value: 'nl',
            required: true,
        },
        {
            icon: <FlagIcon.GreatBritain/>,
            label: 'Engels',
            value: 'en',
            required: true,
        }
    ]

    const handleSendEmailRejectionClick = () => {
        sendTestMail('RejectionEmail', {
            rejectionEmailTitle: formData['rejectionEmailTitle']?.value as string,
            dutchRejectionEmailContent: formData['dutchRejectionEmailContent']?.value as string,
            englishRejectionEmailContent: formData['englishRejectionEmailContent']?.value as string,
            emailSignature: formData['emailSignature']?.value as string,
        });
    }

    const handleSendEmailEnrichmentClick = () => {
        sendTestMail('EnrichmentEmail', {
            enrichmentEmailTitle: formData['enrichmentEmailTitle']?.value as string,
            dutchEnrichmentEmailContent: formData['dutchEnrichmentEmailContent']?.value as string,
            englishEnrichmentEmailContent: formData['englishEnrichmentEmailContent']?.value as string,
            emailSignature: formData['emailSignature']?.value as string,
        });
    }

    const handleSendEmailSignatureClick = () => {
        sendTestMail('ManualEmail', {
            emailSignature: formData['emailSignature']?.value as string,
        });
    }

    return (
        <>
            <div className={styles.contentSection}>
                <div className={styles.descriptionPanel}>
                    <Text
                        color={ColorEnum.Gray800}
                        weight={FontWeightEnum.Bold}
                        tag='h6'
                    >
                        {t('settingsPage.rejection.template.title')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Sm}
                        color={ColorEnum.Gray800}
                        tag='p'
                    >
                        {t('settingsPage.rejection.template.description')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Sm}
                        color={ColorEnum.Gray800}
                        tag='p'
                    >
                        {t('settingsPage.template.variable.description')}
                    </Text>
                    <div>
                        <ButtonNew variant="link" onClick={SidePanel.toggle}>
                            {t('sidepanel.variable.more.about')}
                        </ButtonNew>
                    </div>
                </div>
                <div className={styles.settingsPanel}>
                    <div className={styles.formRow}>
                        <Label required>{t('settingsPage.input.subject')}</Label>
                        <div className={styles.formRow}>
                            {isLoading
                                ?
                                <>
                                    <SkeletonComponent height={40}/>
                                </>
                                :
                                <>
                                    <InputField
                                        name='rejectionEmailTitle'
                                        value={formData['rejectionEmailTitle']?.value as string}
                                    />
                                </>
                            }
                        </div>
                        <FormError errors={formErrors['rejectionEmailTitle']} />
                        <div className={styles.formRow}>
                            {isLoading ? (
                                <SkeletonComponent height={40}/>
                            ) : (
                                <HorizontalTabs
                                    active={rejectionTemplateLangActiveTab}
                                    tabs={languageTabs}
                                    onChange={(tab: TabData) => setRejectionTemplateLangActiveTab(tab.value)}
                                />
                            )}
                        </div>
                        <div className={styles.formRow}>
                            {isLoading ? (
                                <SkeletonComponent height={200}/>
                            ) : (
                                <>
                                    <TextArea
                                        id='dutchRejectionEmailContent'
                                        name='dutchRejectionEmailContent'
                                        value={formData['dutchRejectionEmailContent']?.value as string}
                                        toolbarExtensions={[
                                            {
                                                label: 'variables-dropdown',
                                                options: storedData.rejectionEmailVariables
                                            }
                                        ]}
                                        hidden={rejectionTemplateLangActiveTab !== 'nl'}
                                        required
                                    />
                                    <FormError errors={formErrors['dutchRejectionEmailContent']} />
                                    <TextArea
                                        id='englishRejectionEmailContent'
                                        name='englishRejectionEmailContent'
                                        value={formData['englishRejectionEmailContent']?.value as string}
                                        toolbarExtensions={[
                                            {
                                                label: 'variables-dropdown',
                                                options: storedData.rejectionEmailVariables
                                            }
                                        ]}
                                        hidden={rejectionTemplateLangActiveTab !== 'en'}
                                        required
                                    />
                                    <FormError errors={formErrors['englishRejectionEmailContent']} />
                                </>
                            )}
                        </div>
                        <div className={styles.formRow}>
                            <div className={styles.contentSectionFooter}>
                                <div className={styles.signatureInformation}>
                                    <div className={styles.draftIconContainer}>
                                        <DraftIcon/>
                                    </div>
                                    <Text color={ColorEnum.Gray500} size={FontSizeEnum.Sm}>
                                        {t('settingsPage.signature.extra.information.description')}&nbsp;
                                        <ButtonNew
                                            variant="link"
                                            size="sm"
                                            onClick={() => settingsSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                        >
                                            {t('settingsPage.signature.directory')}
                                        </ButtonNew>
                                    </Text>
                                </div>
                                <div className={styles.contentSectionFooterContent}>
                                    {!isLoading ? (
                                        <>
                                            <div className={styles.fullWidth}>
                                                <Text color={ColorEnum.Gray400}
                                                    size={FontSizeEnum.Sm}>
                                                    {t('settingsPage.signature.testMail.notification', {agentEmail: user.email})}
                                                </Text>
                                            </div>
                                            <ButtonNew
                                                iconBefore={<MailSendLineIcon/>}
                                                variant='secondary-gray'
                                                onClick={handleSendEmailRejectionClick}
                                                // disabled={
                                                //     !validateString(mailingSettings.rejectionEmailTitle) ||
                                                //     !validateString(mailingSettings.dutchRejectionEmailContent) ||
                                                //     !validateString(mailingSettings.englishRejectionEmailContent)
                                                // }
                                            >{t('settingsPage.signature.agencySelector.testEmail.button')}</ButtonNew>
                                        </>
                                    ) : (
                                        <SkeletonComponent width={150} height={40}/>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr/>
            <div className={styles.contentSection}>
                <div className={styles.descriptionPanel}>
                    <Text
                        color={ColorEnum.Gray800}
                        weight={FontWeightEnum.Bold}
                        tag='h6'
                    >
                        {t('settingsPage.enrichment.template.title')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Sm}
                        color={ColorEnum.Gray800}
                        tag='p'
                    >
                        {t('settingsPage.enrichment.template.description')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Sm}
                        color={ColorEnum.Gray800}
                        tag='p'
                    >
                        {t('settingsPage.template.variable.description')}
                    </Text>
                    <div>
                        <ButtonNew variant="link" onClick={SidePanel.toggle}>
                            {t('sidepanel.variable.more.about')}
                        </ButtonNew>
                    </div>
                </div>
                <div className={styles.settingsPanel}>
                    <div className={styles.formRow}>
                        <Label required>{t('settingsPage.input.subject')}</Label>
                        <div className={styles.formRow}>
                            {isLoading
                                ?
                                <>
                                    <SkeletonComponent height={40}/>
                                </>
                                :
                                <>
                                    <InputField
                                        name='enrichmentEmailTitle'
                                        value={formData['enrichmentEmailTitle']?.value as string}
                                    />
                                </>
                            }
                        </div>
                        <FormError errors={formErrors['enrichmentEmailTitle']} />
                        <div className={styles.formRow}>
                            {isLoading ? (
                                <SkeletonComponent height={40}/>
                            ) : (
                                <HorizontalTabs
                                    active={enrichmentTemplateLangActiveTab}
                                    tabs={languageTabs}
                                    onChange={(tab: TabData) => setEnrichmentTemplateLangActiveTab(tab.value)}
                                />
                            )}
                        </div>
                        <div className={styles.formRow}>
                            {isLoading ? (
                                <SkeletonComponent height={200}/>
                            ) : (
                                <>
                                    <TextArea
                                        id='dutchEnrichmentEmailContent'
                                        name='dutchEnrichmentEmailContent'
                                        value={formData['dutchEnrichmentEmailContent']?.value as string}
                                        toolbarExtensions={[
                                            {
                                                label: 'variables-dropdown',
                                                options: storedData.enrichmentEmailVariables
                                            }
                                        ]}
                                        hidden={enrichmentTemplateLangActiveTab !== 'nl'}
                                        required
                                    />
                                    <FormError errors={formErrors['dutchEnrichmentEmailContent']} />
                                    <TextArea
                                        id='englishEnrichmentEmailContent'
                                        name='englishEnrichmentEmailContent'
                                        value={formData['englishEnrichmentEmailContent']?.value as string}
                                        toolbarExtensions={[
                                            {
                                                label: 'variables-dropdown',
                                                options: storedData.enrichmentEmailVariables
                                            }
                                        ]}
                                        hidden={enrichmentTemplateLangActiveTab !== 'en'}
                                        required
                                    />
                                    <FormError errors={formErrors['englishEnrichmentEmailContent']} />
                                </>
                            )}
                        </div>
                        <div className={styles.formRow}>
                            <div className={styles.contentSectionFooter}>
                                <div className={styles.signatureInformation}>
                                    <div className={styles.draftIconContainer}>
                                        <DraftIcon/>
                                    </div>
                                    <Text color={ColorEnum.Gray500} size={FontSizeEnum.Sm}>
                                        {t('settingsPage.signature.extra.information.description')}&nbsp;
                                        <ButtonNew
                                            variant="link"
                                            size="sm"
                                            onClick={() => settingsSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                        >
                                            {t('settingsPage.signature.directory')}
                                        </ButtonNew>
                                    </Text>
                                </div>
                                <div className={styles.contentSectionFooterContent}>
                                    {!isLoading ? (
                                        <>
                                            <div className={styles.fullWidth}>
                                                <Text color={ColorEnum.Gray400}
                                                    size={FontSizeEnum.Sm}>
                                                    {t('settingsPage.signature.testMail.notification', {agentEmail: user.email})}
                                                </Text>
                                            </div>
                                            <ButtonNew
                                                iconBefore={<MailSendLineIcon/>}
                                                variant='secondary-gray'
                                                onClick={handleSendEmailEnrichmentClick}
                                                // disabled={
                                                //     !validateString(mailingSettings.enrichmentEmailTitle) ||
                                                //     !validateString(mailingSettings.dutchEnrichmentEmailContent) ||
                                                //     !validateString(mailingSettings.englishEnrichmentEmailContent)
                                                // }
                                            >{t('settingsPage.signature.agencySelector.testEmail.button')}</ButtonNew>
                                        </>
                                    ) : (
                                        <SkeletonComponent width={150} height={40}/>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr/>
            <div className={styles.contentSection}>
                <div className={styles.descriptionPanel}>
                    <Text
                        color={ColorEnum.Gray800}
                        weight={FontWeightEnum.Bold}
                        tag='h6'
                    >
                        {t('settingsPage.signature.title')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Md}
                        color={ColorEnum.Gray500}
                    >
                        {t('settingsPage.signature.description')}
                        <br/>
                        <ul className={styles.listStyle}>
                            <li>{t('settingsPage.signature.list.first')}</li>
                            <li>{t('settingsPage.signature.list.second')}</li>
                            <li>{t('settingsPage.signature.list.third')}</li>
                        </ul>
                    </Text>
                </div>
                <div ref={settingsSectionRef} className={styles.settingsPanel}>
                    <div className={styles.formRow}>
                        {!isLoading ? (
                            <TextArea
                                id='emailSignature'
                                name='emailSignature'
                                value={formData['emailSignature']?.value as string}
                                allowImages={true}
                                required
                            />
                        ) : (
                            <SkeletonComponent height={200}/>
                        )}
                    </div>
                    <FormError errors={formErrors['emailSignature']} />
                    <div className={styles.formRow}>
                        <div className={styles.contentSectionFooter}>
                            <div className={styles.contentSectionFooterContent}>
                                {!isLoading ? (
                                    <>
                                        <div className={styles.fullWidth}>
                                            <Text color={ColorEnum.Gray400}
                                                size={FontSizeEnum.Sm}>
                                                {t('settingsPage.signature.testMail.notification', {agentEmail: user.email})}
                                            </Text>
                                        </div>
                                        <ButtonNew
                                            iconBefore={<MailSendLineIcon/>}
                                            variant='secondary-gray'
                                            onClick={handleSendEmailSignatureClick}
                                            // disabled={!validateString(mailingSettings.emailSignature)}
                                        >{t('settingsPage.signature.agencySelector.testEmail.button')}</ButtonNew>
                                    </>
                                ) : (
                                    <SkeletonComponent width={150} height={40}/>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmailTemplatesContent;
